import { isValidElement, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { LEGACY_USER_ROLES } from '@shared/auth/roles';

import { RequireRole } from '@core/auth/guards';
import { lazyWithRetry } from '@core/utils/lazyWithRetry';
import { AuthThemeScope } from '@modules/auth/theme/AuthThemeScope';

/**
 * Plain objects, not react-router `RouteObject`s: the shell owns how a route is wrapped
 * (suspense, per-route error boundary, layout), and a `RouteObject` smuggles that decision
 * into the feature list.
 */
export interface ModuleRoute {
  path: string;
  element: ReactNode;
  title: string;
}

// Auth
const SignIn = lazyWithRetry(() => import('@modules/auth/SignIn'));
const SignUp = lazyWithRetry(() => import('@modules/auth/SignUp'));
const ForgotPassword = lazyWithRetry(() => import('@modules/auth/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('@modules/auth/ResetPassword'));
const VerifyEmail = lazyWithRetry(() => import('@modules/auth/VerifyEmail'));

// Public
const PublicLayout = lazyWithRetry(() => import('@modules/public/PublicLayout'));
const HomePage = lazyWithRetry(() => import('@modules/public/HomePage'));
const PublicPropertiesPage = lazyWithRetry(() => import('@modules/public/PublicPropertiesPage'));
const PublicPropertyDetailPage = lazyWithRetry(
  () => import('@modules/public/PublicPropertyDetailPage')
);
const AboutPage = lazyWithRetry(() => import('@modules/public/AboutPage'));
const ContactPage = lazyWithRetry(() => import('@modules/public/ContactPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('@modules/public/PrivacyPolicyPage'));
const TermsPage = lazyWithRetry(() => import('@modules/public/TermsPage'));

const OverviewDashboard = lazyWithRetry(() => import('@modules/dashboard/OverviewDashboard'));
const AccountPage = lazyWithRetry(() => import('@modules/profile/AccountPage'));
const ProfileDetailsPage = lazyWithRetry(() => import('@modules/profile/ProfileDetailsPage'));
const NotificationsPage = lazyWithRetry(() => import('@modules/notifications/NotificationsPage'));

// Seller
const SellerEnquiriesPage = lazyWithRetry(() => import('@modules/enquiries/SellerEnquiriesPage'));
const SellerBuyerEnquiriesPage = lazyWithRetry(
  () => import('@modules/buyer-enquiries/SellerBuyerEnquiriesPage')
);
const SellerActivityPage = lazyWithRetry(() => import('@modules/seller/SellerActivityPage'));
const MyProperties = lazyWithRetry(() => import('@modules/properties/MyProperties'));
const PropertyDetailPage = lazyWithRetry(() => import('@modules/properties/PropertyDetailPage'));

// Admin
const AdminDashboard = lazyWithRetry(() => import('@modules/admin/AdminDashboard'));
const AdminPropertiesPage = lazyWithRetry(() => import('@modules/admin/AdminPropertiesPage'));
const AdminPropertyDetail = lazyWithRetry(() => import('@modules/admin/AdminPropertyDetail'));
const AdminEnquiriesPage = lazyWithRetry(() => import('@modules/enquiries/AdminEnquiriesPage'));
const AdminEnquiryDetail = lazyWithRetry(() => import('@modules/enquiries/AdminEnquiryDetail'));
const AdminDealsPage = lazyWithRetry(() => import('@modules/deals/AdminDealsPage'));
const AdminDealDetail = lazyWithRetry(() => import('@modules/deals/AdminDealDetail'));
const AdminSellersPage = lazyWithRetry(() => import('@modules/admin/AdminSellersPage'));
const AdminLawyersPage = lazyWithRetry(() => import('@modules/admin/AdminLawyersPage'));
const AdminBuyersPage = lazyWithRetry(() => import('@modules/admin/AdminBuyersPage'));
const AdminPropertyManagersPage = lazyWithRetry(
  () => import('@modules/property-managers/AdminPropertyManagersPage')
);
const AdminLegalRequestsPage = lazyWithRetry(() => import('@modules/admin/AdminLegalRequestsPage'));
const AdminBuyerEnquiriesPage = lazyWithRetry(
  () => import('@modules/buyer-enquiries/AdminBuyerEnquiriesPage')
);
const AdminBuyerEnquiryDetail = lazyWithRetry(
  () => import('@modules/buyer-enquiries/AdminBuyerEnquiryDetail')
);
const AdminNotificationsPage = lazyWithRetry(
  () => import('@modules/notifications/AdminNotificationsPage')
);

// Broker
const BrokerPropertiesPage = lazyWithRetry(() => import('@modules/broker/BrokerPropertiesPage'));
const BrokerPropertyDetail = lazyWithRetry(() => import('@modules/broker/BrokerPropertyDetail'));
const BrokerEnquiriesPage = lazyWithRetry(() => import('@modules/enquiries/BrokerEnquiriesPage'));
const BrokerDealsPage = lazyWithRetry(() => import('@modules/deals/BrokerDealsPage'));
const BrokerDealDetailPage = lazyWithRetry(() => import('@modules/deals/BrokerDealDetailPage'));
const SavedPropertiesPage = lazyWithRetry(() => import('@modules/broker/SavedPropertiesPage'));

// Lawyer
const LawyerDashboard = lazyWithRetry(() => import('@modules/lawyer/LawyerDashboard'));
const LawyerPropertiesPage = lazyWithRetry(() => import('@modules/lawyer/LawyerPropertiesPage'));
const LawyerPropertyReview = lazyWithRetry(() => import('@modules/lawyer/LawyerPropertyReview'));
const CertificatesPage = lazyWithRetry(() => import('@modules/legal-opinions/CertificatesPage'));
const LawyerCalendarPage = lazyWithRetry(() => import('@modules/lawyer/LawyerCalendarPage'));
const LawyerProfilePage = lazyWithRetry(() => import('@modules/profile/LawyerProfilePage'));
const LawyerNotificationsPage = lazyWithRetry(
  () => import('@modules/notifications/LawyerNotificationsPage')
);

// Buyer
const BuyerPropertiesPage = lazyWithRetry(() => import('@modules/buyer/BuyerPropertiesPage'));
const BuyerPropertyDetail = lazyWithRetry(() => import('@modules/buyer/BuyerPropertyDetail'));
const BuyerSavedPage = lazyWithRetry(() => import('@modules/buyer/BuyerSavedPage'));
const BuyerEnquiriesPage = lazyWithRetry(
  () => import('@modules/buyer-enquiries/BuyerEnquiriesPage')
);
const BuyerEnquiryDetail = lazyWithRetry(
  () => import('@modules/buyer-enquiries/BuyerEnquiryDetail')
);
const BuyerNegotiationsPage = lazyWithRetry(
  () => import('@modules/negotiations/BuyerNegotiationsPage')
);
const BuyerNegotiationDetail = lazyWithRetry(
  () => import('@modules/negotiations/BuyerNegotiationDetail')
);
const BuyerLegalRequestsPage = lazyWithRetry(
  () => import('@modules/legal-requests/BuyerLegalRequestsPage')
);
const BuyerLegalRequestDetail = lazyWithRetry(
  () => import('@modules/legal-requests/BuyerLegalRequestDetail')
);
const BuyerAlertsPage = lazyWithRetry(() => import('@modules/alerts/BuyerAlertsPage'));

const publicShell = (children: ReactNode) => <PublicLayout>{children}</PublicLayout>;

/** Matched before any auth gate — a guest and a signed-out tab see the same thing. */
export const publicRoutes: ModuleRoute[] = [
  { path: '/home', element: publicShell(<HomePage />), title: 'nav.home' },
  { path: '/properties', element: publicShell(<PublicPropertiesPage />), title: 'nav.properties' },
  {
    path: '/properties/:id',
    element: publicShell(<PublicPropertyDetailPage />),
    title: 'nav.properties'
  },
  { path: '/about', element: publicShell(<AboutPage />), title: 'nav.about' },
  { path: '/contact', element: publicShell(<ContactPage />), title: 'nav.contact' },
  {
    path: '/privacy-policy',
    element: publicShell(<PrivacyPolicyPage />),
    title: 'footer.privacyPolicy'
  },
  { path: '/terms', element: publicShell(<TermsPage />), title: 'footer.termsOfService' }
];

/**
 * Static content reachable from every phase, not gated behind `/home` like `/properties` —
 * a signed-in user clicking Privacy Policy or Terms from the footer must land on these too,
 * not fall through the authenticated shell's catch-all.
 */
export const STATIC_PUBLIC_PATHS = ['/home', '/about', '/contact', '/privacy-policy', '/terms'];

/** `/` renders the login page for a guest — the landing page IS login, not a marketing site. */
export const loginElement = (
  <AuthThemeScope>
    <SignIn />
  </AuthThemeScope>
);

/** The guest tree. A signed-in user is redirected off these by `GuestRoute`. */
export const guestRoutes: ModuleRoute[] = [
  { path: '/login', element: loginElement, title: 'nav.signIn' },
  {
    path: '/register',
    element: (
      <AuthThemeScope>
        <SignUp />
      </AuthThemeScope>
    ),
    title: 'nav.signUp'
  },
  {
    path: '/forgot-password',
    element: (
      <AuthThemeScope>
        <ForgotPassword />
      </AuthThemeScope>
    ),
    title: 'form.forgotPassword'
  },
  {
    path: '/reset-password',
    element: (
      <AuthThemeScope>
        <ResetPassword />
      </AuthThemeScope>
    ),
    title: 'form.resetPassword'
  }
];

/** Reachable only by a signed-in user whose email is still unverified. */
export const verifyEmailElement = <VerifyEmail />;

/**
 * The authenticated shell's routes. Guards compose by nesting JSX — outermost is the
 * coarsest gate — so a role check is visible on the route it protects rather than hidden
 * in a parent `<Route element>` three files away.
 */
export const appRoutes: ModuleRoute[] = [
  {
    path: '/dashboard',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <OverviewDashboard />
      </RequireRole>
    ),
    title: 'nav.overview'
  },
  {
    path: '/account',
    element: (
      <RequireRole role={[...LEGACY_USER_ROLES, 'admin']}>
        <AccountPage />
      </RequireRole>
    ),
    title: 'nav.account'
  },
  {
    path: '/notifications',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <NotificationsPage />
      </RequireRole>
    ),
    title: 'nav.notifications'
  },

  // Seller
  {
    path: '/seller/dashboard',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <Navigate to='/dashboard?tab=seller' replace />
      </RequireRole>
    ),
    title: 'nav.dashboard'
  },
  {
    path: '/seller/enquiries',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <SellerEnquiriesPage />
      </RequireRole>
    ),
    title: 'nav.enquiries'
  },
  {
    path: '/seller/buyer-enquiries',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <SellerBuyerEnquiriesPage />
      </RequireRole>
    ),
    title: 'nav.buyerEnquiries'
  },
  {
    path: '/seller/activity',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <SellerActivityPage />
      </RequireRole>
    ),
    title: 'nav.activity'
  },
  {
    path: '/seller/profile',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <Navigate to='/account' replace />
      </RequireRole>
    ),
    title: 'nav.account'
  },
  {
    path: '/properties',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <MyProperties />
      </RequireRole>
    ),
    title: 'nav.myProperties'
  },
  {
    path: '/properties/:id',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <PropertyDetailPage />
      </RequireRole>
    ),
    title: 'nav.properties'
  },

  // Admin
  {
    path: '/admin/dashboard',
    element: (
      <RequireRole role='admin'>
        <AdminDashboard />
      </RequireRole>
    ),
    title: 'nav.dashboard'
  },
  {
    path: '/admin/properties',
    element: (
      <RequireRole role='admin'>
        <AdminPropertiesPage />
      </RequireRole>
    ),
    title: 'nav.properties'
  },
  {
    path: '/admin/properties/:id',
    element: (
      <RequireRole role='admin'>
        <AdminPropertyDetail />
      </RequireRole>
    ),
    title: 'nav.properties'
  },
  {
    path: '/admin/enquiries',
    element: (
      <RequireRole role='admin'>
        <AdminEnquiriesPage />
      </RequireRole>
    ),
    title: 'nav.enquiries'
  },
  {
    path: '/admin/enquiries/:id',
    element: (
      <RequireRole role='admin'>
        <AdminEnquiryDetail />
      </RequireRole>
    ),
    title: 'nav.enquiries'
  },
  {
    path: '/admin/deals',
    element: (
      <RequireRole role='admin'>
        <AdminDealsPage />
      </RequireRole>
    ),
    title: 'nav.deals'
  },
  {
    path: '/admin/deals/:id',
    element: (
      <RequireRole role='admin'>
        <AdminDealDetail />
      </RequireRole>
    ),
    title: 'nav.deals'
  },
  {
    path: '/admin/sellers',
    element: (
      <RequireRole role='admin'>
        <AdminSellersPage />
      </RequireRole>
    ),
    title: 'nav.sellers'
  },
  {
    path: '/admin/lawyers',
    element: (
      <RequireRole role='admin'>
        <AdminLawyersPage />
      </RequireRole>
    ),
    title: 'nav.lawyers'
  },
  {
    path: '/admin/buyers',
    element: (
      <RequireRole role='admin'>
        <AdminBuyersPage />
      </RequireRole>
    ),
    title: 'nav.buyers'
  },
  {
    path: '/admin/property-managers',
    element: (
      <RequireRole role='admin'>
        <AdminPropertyManagersPage />
      </RequireRole>
    ),
    title: 'nav.propertyManagers'
  },
  {
    path: '/admin/legal-requests',
    element: (
      <RequireRole role='admin'>
        <AdminLegalRequestsPage />
      </RequireRole>
    ),
    title: 'nav.legalRequests'
  },
  {
    path: '/admin/buyer-enquiries',
    element: (
      <RequireRole role='admin'>
        <AdminBuyerEnquiriesPage />
      </RequireRole>
    ),
    title: 'nav.buyerEnquiries'
  },
  {
    path: '/admin/buyer-enquiries/:id',
    element: (
      <RequireRole role='admin'>
        <AdminBuyerEnquiryDetail />
      </RequireRole>
    ),
    title: 'nav.buyerEnquiries'
  },
  {
    path: '/admin/notifications',
    element: (
      <RequireRole role='admin'>
        <AdminNotificationsPage />
      </RequireRole>
    ),
    title: 'nav.notifications'
  },

  // Broker
  {
    path: '/broker/dashboard',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <Navigate to='/dashboard?tab=broker' replace />
      </RequireRole>
    ),
    title: 'nav.dashboard'
  },
  {
    path: '/broker/properties',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BrokerPropertiesPage />
      </RequireRole>
    ),
    title: 'nav.properties'
  },
  {
    path: '/broker/properties/:id',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BrokerPropertyDetail />
      </RequireRole>
    ),
    title: 'nav.properties'
  },
  {
    path: '/broker/enquiries',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BrokerEnquiriesPage />
      </RequireRole>
    ),
    title: 'nav.enquiries'
  },
  {
    path: '/broker/deals',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BrokerDealsPage />
      </RequireRole>
    ),
    title: 'nav.deals'
  },
  {
    path: '/broker/deals/:id',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BrokerDealDetailPage />
      </RequireRole>
    ),
    title: 'nav.deals'
  },
  {
    path: '/broker/saved',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <SavedPropertiesPage />
      </RequireRole>
    ),
    title: 'nav.saved'
  },
  {
    path: '/broker/profile',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <ProfileDetailsPage />
      </RequireRole>
    ),
    title: 'nav.profile'
  },

  // Lawyer
  {
    path: '/lawyer/dashboard',
    element: (
      <RequireRole role='lawyer'>
        <LawyerDashboard />
      </RequireRole>
    ),
    title: 'nav.dashboard'
  },
  {
    path: '/lawyer/properties',
    element: (
      <RequireRole role='lawyer'>
        <LawyerPropertiesPage />
      </RequireRole>
    ),
    title: 'nav.properties'
  },
  {
    path: '/lawyer/properties/:id/review',
    element: (
      <RequireRole role='lawyer'>
        <LawyerPropertyReview />
      </RequireRole>
    ),
    title: 'nav.properties'
  },
  {
    path: '/lawyer/certificates',
    element: (
      <RequireRole role='lawyer'>
        <CertificatesPage />
      </RequireRole>
    ),
    title: 'nav.certificates'
  },
  {
    path: '/lawyer/calendar',
    element: (
      <RequireRole role='lawyer'>
        <LawyerCalendarPage />
      </RequireRole>
    ),
    title: 'nav.calendar'
  },
  {
    path: '/lawyer/profile',
    element: (
      <RequireRole role='lawyer'>
        <LawyerProfilePage />
      </RequireRole>
    ),
    title: 'nav.profile'
  },
  {
    path: '/lawyer/notifications',
    element: (
      <RequireRole role='lawyer'>
        <LawyerNotificationsPage />
      </RequireRole>
    ),
    title: 'nav.notifications'
  },

  // Buyer
  {
    path: '/buyer/dashboard',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <Navigate to='/dashboard?tab=buyer' replace />
      </RequireRole>
    ),
    title: 'nav.dashboard'
  },
  {
    path: '/buyer/properties',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerPropertiesPage />
      </RequireRole>
    ),
    title: 'nav.browseProperties'
  },
  {
    path: '/buyer/properties/:id',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerPropertyDetail />
      </RequireRole>
    ),
    title: 'nav.browseProperties'
  },
  {
    path: '/buyer/saved',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerSavedPage />
      </RequireRole>
    ),
    title: 'nav.saved'
  },
  {
    path: '/buyer/enquiries',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerEnquiriesPage />
      </RequireRole>
    ),
    title: 'nav.enquiries'
  },
  {
    path: '/buyer/enquiries/:id',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerEnquiryDetail />
      </RequireRole>
    ),
    title: 'nav.enquiries'
  },
  {
    path: '/buyer/negotiations',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerNegotiationsPage />
      </RequireRole>
    ),
    title: 'nav.negotiations'
  },
  {
    path: '/buyer/negotiations/:id',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerNegotiationDetail />
      </RequireRole>
    ),
    title: 'nav.negotiations'
  },
  {
    path: '/buyer/legal-requests',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerLegalRequestsPage />
      </RequireRole>
    ),
    title: 'nav.legalRequests'
  },
  {
    path: '/buyer/legal-requests/:id',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerLegalRequestDetail />
      </RequireRole>
    ),
    title: 'nav.legalRequests'
  },
  {
    path: '/buyer/alerts',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <BuyerAlertsPage />
      </RequireRole>
    ),
    title: 'nav.alerts'
  },
  {
    path: '/buyer/profile',
    element: (
      <RequireRole role={LEGACY_USER_ROLES}>
        <ProfileDetailsPage />
      </RequireRole>
    ),
    title: 'nav.profile'
  }
];

type Preloadable = { preload?: () => Promise<unknown> };

function preloadOf(node: ReactNode): (() => Promise<unknown>) | undefined {
  if (Array.isArray(node)) {
    for (const child of node) {
      const found = preloadOf(child);
      if (found) return found;
    }
    return undefined;
  }
  if (!isValidElement(node)) return undefined;
  const own = (node.type as Preloadable).preload;
  return own ?? preloadOf((node.props as { children?: ReactNode }).children);
}

const preloaders = new Map<string, () => Promise<unknown>>();
for (const route of [...appRoutes, ...publicRoutes, ...guestRoutes]) {
  const preload = preloadOf(route.element);
  if (preload) preloaders.set(route.path, preload);
}

export const routePreloaders: ReadonlyMap<string, () => Promise<unknown>> = preloaders;

export function preloadRoute(path: string): void {
  // A failed warm-up stays silent; the real navigation retries through the same factory.
  preloaders
    .get(path)?.()
    .catch(() => {});
}
